/**
 * Coaching review module: the shapes shared by the Cloud Functions, the mobile
 * app and the coach portal. Pure types, no runtime imports, so every side
 * compiles this file as its own. Timestamps are generic (`TS`) because the
 * server holds admin Timestamps, the clients hold client Timestamps, and the
 * pure logic in this folder only ever sees epoch milliseconds.
 *
 * A submission is a bucket: an ordered list of clips, each a window of one of
 * the player's recordings, reviewed as one continuous piece of footage. A
 * player builds the bucket in a draft, one per club, and submits it to one
 * coach at that club.
 */

export const SUBMISSION_STATUSES = [
    "submitted",
    "accepted",
    "in_review",
    "returned",
    "declined",
    "expired",
    "cancelled",
] as const;
export type SubmissionStatus = typeof SUBMISSION_STATUSES[number];

/** Statuses that still hold a coach's attention or a player's place in the queue. */
export const OPEN_STATUSES: readonly SubmissionStatus[] = ["submitted", "accepted", "in_review"];
export const TERMINAL_STATUSES: readonly SubmissionStatus[] = ["returned", "declined", "expired", "cancelled"];

export const CLOSED_REASONS = [
    "coach_declined",
    "coach_no_response",
    "review_overdue",
    "player_cancelled",
    "clip_failed",
    "admin",
] as const;
export type ClosedReason = typeof CLOSED_REASONS[number];

export type Actor = "player" | "coach" | "system" | "admin";

export interface StatusHistoryEntry<TS> {
    status: SubmissionStatus;
    at: TS;
    by: Actor;
    reason?: string;
}

/**
 * A window of one recording. Times are seconds into the SOURCE recording and
 * are always snapped to segment edges by the server, so the length the player
 * sees, the bucket budget and the overlap rule all describe the same bytes.
 */
export interface ClipWindow {
    id: string;
    sessionId: string;
    title: string;
    startSec: number;
    endSec: number;
    durationSec: number;
}

/** A clip in a draft: a reference only, nothing is cut until submission. */
export interface DraftClip<TS> extends ClipWindow {
    sourceCompletedAt: TS;
    /** When the recording leaves retention; null when it is protected and never will. */
    sourceExpiresAt: TS | null;
    addedAt: TS;
}

/** What a player wants help with. Stored as keys; the labels are what they read. */
export const FOCUS_AREAS = ["serve", "return", "volleys", "smash", "lob", "positioning", "movement", "tactics", "other"] as const;
export type FocusArea = typeof FOCUS_AREAS[number];
export const FOCUS_LABELS: Record<FocusArea, string> = {
    serve: "Serve",
    return: "Return",
    volleys: "Volleys",
    smash: "Smash",
    lob: "Lob",
    positioning: "Positioning",
    movement: "Movement",
    tactics: "Tactics",
    other: "Something else",
};
export const isFocusArea = (v: unknown): v is FocusArea => typeof v === "string" && (FOCUS_AREAS as readonly string[]).includes(v);

/**
 * A player's request in progress at one club. Id `{playerId}_{clubId}`. The
 * details fields are written by step 3 of the stepper; a draft made before
 * phase 3 has none, so readers treat a missing one as empty.
 */
export interface CoachingDraft<TS> {
    playerId: string;
    clubId: string;
    clubName: string;
    clips: DraftClip<TS>[];
    totalSec: number;
    focusAreas?: FocusArea[];
    note?: string;
    coachId?: string | null;
    createdAt: TS;
    updatedAt: TS;
    /** The earlier of the draft age limit and the first source expiry. */
    expiresAt: TS;
    schemaVersion: 1;
}

export type CutStatus = "pending" | "ready" | "failed";
export type CutMode = "copy" | "transcode";

export interface ClipCut {
    status: CutStatus;
    /** Pixel size of the encoded video, needed to fit annotations to the frame. */
    width: number | null;
    height: number | null;
    mode: CutMode | null;
    /** Short operator-facing reason when status is failed. Never shown raw to players. */
    failureCode: string | null;
}

/** A clip in a submission. `order` is its place in the bucket, from 0. */
export interface SubmissionClip extends ClipWindow {
    order: number;
    cut: ClipCut;
}

/** Aggregate of the clips' cuts, so the UI reads one field. */
export interface BucketCut {
    status: CutStatus;
    readyCount: number;
}

export interface SubmissionTiming<TS> {
    createdAt: TS;
    submittedAt: TS;
    respondByAt: TS;
    acceptedAt: TS | null;
    dueAt: TS | null;
    returnedAt: TS | null;
    closedAt: TS | null;
    /** When the clips, moments and record are deleted together. */
    purgeAt: TS | null;
    /** Set once the sweep has reminded the coach to answer. */
    reminderSentAt: TS | null;
    /** Set once the sweep has reminded the coach the review is due soon. Absent on phase 2 docs. */
    dueReminderSentAt?: TS | null;
}

export interface SubmissionReview<TS> {
    summary: string;
    summaryVoice?: VoiceNote | null;
    momentCount: number;
    updatedAt: TS | null;
    /** Bumped on every draft save so a second tab can tell it is stale. */
    revision: number;
}

export interface CoachingSubmission<TS> {
    playerId: string;
    playerDisplayName: string;
    coachId: string;
    coachDisplayName: string;
    clubId: string;
    clubName: string;
    clips: SubmissionClip[];
    clipCount: number;
    totalSec: number;
    cut: BucketCut;
    note: string;
    /** What the player asked for help with. Absent on submissions made before phase 3. */
    focusAreas?: FocusArea[];
    status: SubmissionStatus;
    closedReason: ClosedReason | null;
    declineMessage: string | null;
    statusHistory: StatusHistoryEntry<TS>[];
    review: SubmissionReview<TS>;
    timing: SubmissionTiming<TS>;
    playerViewedAt: TS | null;
    /** No longer written: the due time is set at sending from the config promise. On phase 2 docs only. */
    turnaroundHours?: number;
    /** What the player paid. Absent on requests sent before payments, or sent free while payments were off. */
    payment?: SubmissionPayment;
    schemaVersion: 2;
}

/**
 * `paid` once the store confirmed it; `refund_due` when the request closed
 * without a review; `refunded` once the store refunded it. The receipt ids are
 * on the server-only private payment doc, not here, because the coach can read
 * the submission.
 */
export const PAYMENT_STATUSES = ["paid", "refund_due", "refunded"] as const;
export type PaymentStatus = typeof PAYMENT_STATUSES[number];

export const isPaymentStatus = (v: unknown): v is PaymentStatus => typeof v === "string" && (PAYMENT_STATUSES as readonly string[]).includes(v);

export interface SubmissionPayment {
    status: PaymentStatus;
    pricePence: number;
    productId: string;
    /** "APP_STORE", "PLAY_STORE", as RevenueCat names them. */
    store: string | null;
    /** A store test purchase: only testers can make one count. */
    sandbox: boolean;
}

/** A day of the week, 0 Sunday to 6 Saturday, as in `Date.getDay()`. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type Point = { x: number; y: number };

/**
 * A shape in frame space: every coordinate is a fraction of the VIDEO FRAME
 * (0..1 across its width and height), never of the screen. A circle's radius
 * is a fraction of the frame width on both axes, so it stays round whatever
 * the frame's aspect ratio. See annotations.ts for the fitting maths.
 */
export type Shape = (
    | { kind: "path"; points: Point[]; color: string; width: number }
    | { kind: "line"; x1: number; y1: number; x2: number; y2: number; color: string; width: number }
    | { kind: "arrow"; x1: number; y1: number; x2: number; y2: number; color: string; width: number }
    | { kind: "circle"; cx: number; cy: number; r: number; color: string; width: number }
    | { kind: "rect"; x: number; y: number; w: number; h: number; color: string; width: number }
) & {
    /**
     * Seconds into the moment's voice note when the coach drew it, so the player
     * sees it appear as it is spoken about. Absent: shown from the start.
     */
    at?: number;
};

export type ShapeKind = Shape["kind"];

/**
 * A coach's spoken note, stored as AAC at `coaching/{submissionId}/voice/{key}` and
 * served under the same signature as the clips. Only the file name travels.
 */
export interface VoiceNote {
    key: string;
    durationSec: number;
}

export interface Moment {
    id: string;
    /** The clip the frame belongs to. */
    clipId: string;
    /** Seconds into that clip where the frame is held. */
    t: number;
    /** How long the overlay stays before playback resumes, seconds. */
    holdSec: number;
    label: string;
    note: string;
    shapes: Shape[];
    order: number;
    /** Played while the frame is held; the hold lasts at least as long. */
    voice?: VoiceNote | null;
}

export interface ReviewDraft {
    summary: string;
    /** A spoken summary, played from the summary card. */
    summaryVoice?: VoiceNote | null;
    moments: Moment[];
    /** The revision the client last loaded; the server rejects a stale save. */
    baseRevision: number;
}

export interface CoachProfile<TS> {
    displayName: string;
    photoUrl: string | null;
    headline: string;
    bio: string;
    experienceYears: number | null;
    specialties: string[];
    certifications: string[];
    languages: string[];
    clubIds: string[];
    acceptingSubmissions: boolean;
    /** Working days the coach promises a review within. Absent on docs from before coaches set their own. */
    turnaroundDays?: number;
    /** The days the coach works. Absent means every day. */
    workingDays?: Weekday[];
    /** How players with a request can reach the coach. Required when the coach saves their profile; absent on older docs. */
    contactEmail?: string;
    contactPhone?: string | null;
    /** No longer used. Still on coach docs written before coaches set their turnaround in days. */
    turnaroundHours?: number;
    maxOpen: number | null;
    openCount: number;
    /** Reserved for payments. No UI reads it yet. */
    ratePence: number | null;
    /** The coach's price tier in pence (999, 1299 or 1499). Absent or null takes the club's, then the default. */
    priceTier?: number | null;
    createdAt: TS;
    updatedAt: TS;
}

export type CoachClubStatus = "active" | "pending" | "revoked";

export interface CoachClub<TS> {
    coachId: string;
    clubId: string;
    clubName: string;
    status: CoachClubStatus;
    title: string | null;
    approvedBy: string | null;
    approvedAt: TS | null;
    revokedAt: TS | null;
}

/** What a player sees when choosing a coach: the profile minus anything private. */
export interface CoachListing {
    coachId: string;
    displayName: string;
    photoUrl: string | null;
    headline: string;
    bio: string;
    experienceYears: number | null;
    specialties: string[];
    certifications: string[];
    languages: string[];
    turnaroundDays: number;
    workingDays: Weekday[];
    /** When a request sent now would be due, so the app can say the date without counting days itself. */
    dueIfSentNowMs: number;
    /** False when the coach is full or paused; still listed so the player knows why. Kept for app builds that predate `availability`. */
    available: boolean;
    availability: CoachAvailability;
    /** What a review with this coach costs, from their club's price tier. */
    pricePence: number;
}

/** Someone a player can ask about a request. Either way of reaching them may be missing. */
export interface ContactCard {
    name: string;
    email: string | null;
    phone: string | null;
}

/** Who a player can ask about one request: the coach directly, or the club. */
export interface SubmissionContacts {
    coach: ContactCard;
    club: ContactCard;
}

/** Open takes requests; full has reached its limit of waiting plus in-progress requests; paused has switched intake off. */
export type CoachAvailability = "open" | "full" | "paused";

/** One clip's playback source, as getCoachingMedia returns it. */
export interface ClipMedia {
    clipId: string;
    order: number;
    title: string;
    /** HLS playlist, signed for the submission's prefix; segments inherit the signature. */
    playlistUrl: string;
    width: number | null;
    height: number | null;
    durationSec: number;
    startSec: number;
    endSec: number;
}

export interface SubmissionMedia {
    clips: ClipMedia[];
    /** Where voice notes are: `${baseUrl}${key}?${query}`. Absent from servers before voice notes. */
    voice?: { baseUrl: string; query: string };
    /** Unix seconds. Clients fetch again before this passes. */
    expiresAt: number;
}

export const NOTIFICATION_TYPES = {
    submissionReceived: "coaching_submission_received",
    accepted: "coaching_accepted",
    declined: "coaching_declined",
    returned: "coaching_returned",
    expired: "coaching_expired",
    clipFailed: "coaching_clip_failed",
    reminder: "coaching_reminder",
} as const;
export type CoachingNotificationType = typeof NOTIFICATION_TYPES[keyof typeof NOTIFICATION_TYPES];
